import React, { useEffect, useState } from 'react';

import { Box, VStack, Image, Button } from '@gluestack-ui/themed';

import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../auth/store';
import useAppStateStore from '../context/AppStateStore';

import UserMenu from './components/UserMenu';
import NavButton from './components/NavButton';
import { useTenantSettingsStore } from '../context/TenantSettingsStore';
import { useTaskSubmissionState } from '../../student/task/stores/TaskSubmissionStore';
import { useDialog } from '../context/DialogContext';

const WebSidebar = ({ visible, icons, navigationRef }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  const selectedModule = useAppStateStore((state) => state.selectedModule);
  const selectedTaskIds = useAppStateStore((state) => state.selectedTaskIds);

  // Track current route name with state to trigger re-renders
  const [currentRouteName, setCurrentRouteName] = useState(null);

  const { t } = useTranslation();
  const { dirty: isTaskDirty } = useTaskSubmissionState();
  const { showDialog } = useDialog();

  // Listen to navigation state changes
  useEffect(() => {
    if (!navigationRef.current) return;

    // Get initial route name
    const initialState = navigationRef.current.getRootState();
    if (initialState) {
      setCurrentRouteName(initialState.routes[initialState.index]?.name);
    }

    // Listen for navigation state changes
    const unsubscribe = navigationRef.current.addListener('state', (e) => {
      const currentRoute = e?.data?.state?.routes?.[e.data.state.index];
      setCurrentRouteName(currentRoute?.name);
    });

    return unsubscribe;
  }, [navigationRef]);

  const confirmNavigation = (target, params) => {
    if (isTaskDirty) {
      showDialog({
        title: t('student.task.composer.unsaved-dialog.title'),
        message: t('student.task.composer.unsaved-dialog.message'),
        buttons: [
          { text: t('student.task.composer.unsaved-dialog.button-stay'), action: 'secondary' },
          {
            text: t('student.task.composer.unsaved-dialog.button-discard'),
            style: 'destructive',
            onPress: () => navigationRef.current.navigate(target, params),
          },
        ],
      });
    } else {
      navigationRef.current.navigate(target, params);
    }
  };

  const settings = useTenantSettingsStore((state) => state.settings);
  const navbarIconUrl = settings?.imageAssetUrls?.navbarIconUrl;

  if (!visible || !isAuthenticated) return null;

  const handleLogout = () => {
    logout();
  };

  return (
    <VStack
      bg="$bgApp"
      width={72}
      height="100%"
      alignItems="center"
      justifyContent="space-between"
      py={28}
      borderRightWidth={1}
      borderRightColor="$borderLight"
    >
      <VStack alignItems="center">
        <Button variant="link" size="lg" p="$2.5" mb={16}>
          <Image source={{ uri: navbarIconUrl }} alt="Logo" width={38} height={38} />
        </Button>
        <VStack space="lg">
          <NavButton
            title={t('navigation.main.CourseScreen')}
            icon={icons.CourseScreen}
            onPress={() => confirmNavigation('CourseScreen')}
            active={currentRouteName === 'CourseScreen'}
          />
          {selectedModule && (
            <>
              <NavButton
                title={t('navigation.main.ModuleScreen')}
                icon={icons.ModuleScreen}
                onPress={() =>
                  confirmNavigation('ModuleScreen', {
                    moduleId: selectedModule.moduleId,
                    courseId: selectedModule.courseId,
                  })
                }
                active={currentRouteName === 'ModuleScreen'}
              />
              {selectedModule && selectedTaskIds[selectedModule.moduleId] && (
                <NavButton
                  title={t('navigation.main.TaskScreen')}
                  icon={icons.TaskScreen}
                  onPress={() =>
                    confirmNavigation('TaskScreen', {
                      moduleId: selectedModule.moduleId,
                      courseId: selectedModule.courseId,
                      taskId: selectedTaskIds[selectedModule.moduleId],
                    })
                  }
                  active={currentRouteName === 'TaskScreen'}
                />
              )}
            </>
          )}
        </VStack>
      </VStack>

      <Box>
        <UserMenu user={user} logout={handleLogout} orientation="right bottom" />
      </Box>
    </VStack>
  );
};

export default WebSidebar;
